import React, { ReactNode, useCallback, useMemo, useState } from "react";
import { matchPath, useLocation, useNavigate, useSearchParams } from "react-router";

import { useQueryClient } from "@tanstack/react-query";

import { ClientsFilterType } from "@/pages/clients/clients/Clients.types";
import {
  buildClientsFilterSearchParams,
  getClientsFilterParam,
  mapClientsFilterFromSearchParams,
  stringifyClientsFilterValue,
} from "@/pages/clients/clients/Clients.utils";
import { QueryKey } from "@/query/Query";
import { AppRoutes } from "@/router/AppRoutes";

import ClientsSearchContext from "./ClientsSearchContext";

interface Props {
  children: ReactNode;
}

const ClientsSearchContextProvider: React.FC<Props> = ({ children }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [lastSearchParams, setLastSearchParams] = useState<ClientsFilterType | null>(() => {
    const isOnClientsRoute = matchPath({ path: AppRoutes.Clients, end: true }, location.pathname) !== null;
    return isOnClientsRoute ? mapClientsFilterFromSearchParams(searchParams) : null;
  });

  const isClientsListRoute = useMemo(
    () => matchPath({ path: AppRoutes.Clients, end: true }, location.pathname) !== null,
    [location.pathname]
  );

  const filters: ClientsFilterType | null = useMemo(() => {
    if (!isClientsListRoute) {
      return null;
    }
    return mapClientsFilterFromSearchParams(searchParams);
  }, [isClientsListRoute, searchParams]);

  const invalidateClientsQuery = useCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: [QueryKey.Clients],
      exact: false,
      refetchType: "none",
    });
  }, [queryClient]);

  const handleFilterUpdate = useCallback(
    async <T extends keyof ClientsFilterType>(key: T, value: ClientsFilterType[T]) => {
      const param = getClientsFilterParam(key);
      const stringValue = stringifyClientsFilterValue(value);

      if (isClientsListRoute) {
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);

          if (stringValue === null) {
            next.delete(param);
          } else {
            next.set(param, stringValue);
          }

          return next;
        });
        setLastSearchParams({ ...((filters ?? {}) as ClientsFilterType), [key]: value });
        return;
      }

      const params = new URLSearchParams();
      if (stringValue !== null) {
        params.set(param, stringValue);
      }

      setLastSearchParams(
        stringValue !== null ? ({ [key]: value } as unknown as ClientsFilterType) : ({} as ClientsFilterType)
      );
      await navigate({
        pathname: AppRoutes.Clients,
        search: params.toString(),
      });
    },
    [filters, isClientsListRoute, navigate, setSearchParams]
  );

  const handleFiltersUpdate = useCallback(
    async (newFilters: ClientsFilterType) => {
      const params = buildClientsFilterSearchParams(newFilters);

      setLastSearchParams(newFilters);

      if (isClientsListRoute) {
        setSearchParams(params);
        return;
      }

      await navigate({
        pathname: AppRoutes.Clients,
        search: params.toString(),
      });
    },
    [isClientsListRoute, navigate, setSearchParams]
  );

  const removeAllFilters = useCallback(() => {
    if (!isClientsListRoute) {
      return;
    }

    setSearchParams(new URLSearchParams());
    setLastSearchParams({} as ClientsFilterType);
  }, [isClientsListRoute, setSearchParams]);

  const contextValue = useMemo(
    () => ({
      filters,
      lastSearchParams,
      searchParams,
      handleFiltersUpdate,
      handleFilterUpdate,
      removeAllFilters,
      invalidateClientsQuery,
    }),
    [
      filters,
      lastSearchParams,
      searchParams,
      handleFiltersUpdate,
      handleFilterUpdate,
      removeAllFilters,
      invalidateClientsQuery,
    ]
  );

  return <ClientsSearchContext.Provider value={contextValue}>{children}</ClientsSearchContext.Provider>;
};

export default ClientsSearchContextProvider;
